/**
 * THE SIGN-IN WIRE, AND THE ONLY PLACE IT IS KNOWN.
 *
 * Paths and request bodies are typed from `src/api/generated/pharmos-api.ts`,
 * so a revision to the backend's document fails here at compile time rather
 * than inside a screen. The RESPONSE is validated with zod as well, because the
 * document declares every payload member optional (`payload?`, `authToken?`)
 * and a screen cannot be written against "possibly absent everything": a body
 * that does not match is a CONTRACT error, which is a truer thing to say than
 * rendering half a session.
 *
 * THE ENVELOPE. Every success is `{ success, code, message, payload }`. The
 * envelope's own `code`/`message` are the backend's, not ours; nothing here
 * switches on them — see `docs/DECISIONS.md`, error codes are deferred.
 *
 * THE ORDER, from the document's own descriptions:
 *   1. POST /v1/user/login              → interim token, the branches, isPasswordExpired
 *   2. POST /v1/user/login/reset-password   (only when isPasswordExpired)
 *   3. POST /v1/user/login/branches/{code}  → the working token
 */
import { z } from "zod";
import { createApiClient } from "@/api/client";
import { AppError } from "@/api/problems";
import type { paths } from "@/api/generated/pharmos-api";
import type { DisplayLang, ScreenLayout } from "@/api/preference";

const client = createApiClient<paths>();

/* ── the document's schemas ──────────────────────────────────────────────── */

const roleSchema = z.object({
  roleCode: z.string().optional(),
  roleName: z.string().optional(),
});

const grantedBranchSchema = z.object({
  status: z.string().optional(),
  branchCode: z.string(),
  branchNameEn: z.string().optional(),
  branchNameZh: z.string().optional(),
  roles: z.array(roleSchema).optional(),
});

const companySchema = z.object({
  status: z.string().optional(),
  companyNameEn: z.string().optional(),
  companyNameZh: z.string().optional(),
  contractStartDate: z.string().optional(),
  contractEndDate: z.string().optional(),
});

const userSchema = z.object({
  staffId: z.string(),
  status: z.string(),
  /**
   * The two preferences the account carries, both `required` in the document
   * and so both required here — the same call this file makes for `status`.
   *
   * The document types them as bare strings on `UserObj` while typing them as
   * enums (`EN|ZH`, `BROWSER|WINGRID`) on the request that WRITES them, so a
   * read cannot be narrowed at the boundary. Neither is switched on raw:
   * `localeFromDisplayLang` and `viewFromScreenLayout` each map an unrecognised
   * value onto a documented default rather than onto a broken screen. Asked of
   * the backend team in `GAPS.md`.
   */
  displayLang: z.string(),
  screenLayout: z.string(),
  staffNameEn: z.string().optional(),
  staffNameZh: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  isPharmacist: z.boolean(),
  pharmacistRegNumber: z.string().optional(),
  employmentFirstDay: z.string().optional(),
  employmentLastDay: z.string().optional(),
});

const loginPayloadSchema = userSchema.extend({
  company: companySchema.optional(),
  branches: z.array(grantedBranchSchema).optional(),
  isPasswordExpired: z.boolean().optional(),
  authToken: z.string(),
});

const loginBranchPayloadSchema = z.object({ authToken: z.string() });

export type LoginPayload = z.infer<typeof loginPayloadSchema>;
export type GrantedBranch = z.infer<typeof grantedBranchSchema>;
export type UserProfile = z.infer<typeof userSchema>;

/* ── the envelope ────────────────────────────────────────────────────────── */

/** Pulls `payload` out of the envelope and proves its shape, or says the contract moved. */
function payloadOf<T>(schema: z.ZodType<T>, body: unknown, path: string): T {
  const payload =
    body && typeof body === "object" && "payload" in body
      ? (body as { payload?: unknown }).payload
      : undefined;
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new AppError("contract", {
      cause: parsed.error,
      message: `${path} returned a payload this build does not expect`,
    });
  }
  return parsed.data;
}

/* ── the calls ───────────────────────────────────────────────────────────── */

export interface Credentials {
  /** The company id. The document calls it `tenantId` and says it is the company id. */
  tenantId: string;
  username: string;
  password: string;
}

/** Step 1. Proves who you are. The token it returns opens only steps 2 and 3. */
export async function login(credentials: Credentials): Promise<LoginPayload> {
  const { data } = await client.POST("/v1/user/login", { body: credentials });
  return payloadOf(loginPayloadSchema, data, "/v1/user/login");
}

/**
 * Step 2, only when step 1 answered `isPasswordExpired`. Spends the one-time
 * password that is already proven by the interim token, so the new one is the
 * whole body.
 */
export async function resetExpiredPassword(newPassword: string): Promise<LoginPayload> {
  const { data } = await client.POST("/v1/user/login/reset-password", { body: { newPassword } });
  return payloadOf(loginPayloadSchema, data, "/v1/user/login/reset-password");
}

/** Step 3. Binds the session to one branch and returns the token everything else uses. */
export async function chooseBranch(branchCode: string): Promise<string> {
  const { data } = await client.POST("/v1/user/login/branches/{branchCode}", {
    params: { path: { branchCode } },
  });
  return payloadOf(loginBranchPayloadSchema, data, "/v1/user/login/branches/{branchCode}").authToken;
}

/** Ends the session server-side. The caller clears the token whatever this answers. */
export async function logout(): Promise<void> {
  await client.POST("/v1/user/logout", {});
}

/**
 * The account behind the current token. This is the ONLY read-back the contract
 * offers, and it carries no company, branch, roles or expiry — so it proves the
 * token still works and nothing else. See `docs/features/settings/GAPS.md` B5.
 */
export async function readProfile(): Promise<UserProfile> {
  const { data } = await client.GET("/v1/user/profile", {});
  return payloadOf(userSchema, data, "/v1/user/profile");
}

/** Changes a password the operator already knows. Not part of signing in. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await client.PUT("/v1/user/password", { body: { currentPassword, newPassword } });
}

/**
 * The two preferences that belong to the PERSON — their language and their
 * screen layout.
 *
 * BOTH ARE SENT EVERY TIME BECAUSE THE DOCUMENT REQUIRES BOTH. There is no way
 * to say "change my language and leave my layout alone", so every caller has
 * to know the current value of the one it is not changing and send it back
 * unchanged. That is a read-modify-write with no compare-and-set under it: two
 * terminals changing different preferences in the same minute, and the second
 * save silently undoes the first.
 *
 * Accepted as it stands (`GAPS.md` B6, 21-09-2026: "fine, and absorbed").
 * These are one person's own two settings, changed by hand, so the race needs
 * the same person at two terminals in the same second. A re-read before every
 * write would double the requests to narrow a race it still could not close,
 * so the caller passes the whole pair, and `app-header.tsx` takes the one it
 * is not changing off the session it already holds.
 */
export async function savePreference(preference: {
  displayLang: DisplayLang;
  screenLayout: ScreenLayout;
}): Promise<void> {
  await client.PUT("/v1/user/preference", { body: preference });
}
